import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, X, Save, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useStore } from "@/store/useStore";
import { CATEGORY_LABELS, catLabel, mediaUrl } from "@/components/blog/BlogFeed";
import { API_URL } from "@/config/api";

const empty = { title: "", excerpt: "", content: "", category: "news", lang: "ru", cover_url: "", video_url: "", tags: "", publish_at: "" };

export default function BlogEditorPage() {
  const { postId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useStore();
  const [form, setForm] = useState({ ...empty, lang: i18n.language });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [progress, setProgress] = useState(0);
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!postId) return;
    fetch(`${API_URL}/api/blog/posts/${postId}`, { headers }).then((r) => r.json()).then((p) =>
      setForm({ title: p.title, excerpt: p.excerpt || "", content: p.content, category: p.category, lang: p.lang, cover_url: p.cover_url || "",
        video_url: p.video_url || "", tags: (p.tags || []).join(", "), publish_at: p.published_at ? p.published_at.slice(0, 16) : "" }));
  }, [postId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = (kind) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(kind); setProgress(0);
    const fd = new FormData(); fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/blog/upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => ev.lengthComputable && setProgress(Math.round((ev.loaded / ev.total) * 100));
    xhr.onload = () => {
      setUploading(null);
      if (xhr.status >= 200 && xhr.status < 300) {
        const d = JSON.parse(xhr.responseText);
        setForm((f) => ({ ...f, [kind === "cover" ? "cover_url" : "video_url"]: d.url }));
        toast.success(t("blog.uploaded"));
      } else {
        let msg = t("common.error"); try { msg = JSON.parse(xhr.responseText).detail || msg; } catch (_) {}
        toast.error(msg);
      }
    };
    xhr.onerror = () => { setUploading(null); toast.error(t("common.error")); };
    xhr.send(fd);
  };

  const submit = async (status) => {
    if (form.title.trim().length < 3 || form.content.trim().length < 10) return toast.error(t("blog.fillRequired"));
    setSaving(true);
    try {
      const body = { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        cover_url: form.cover_url || null, video_url: form.video_url || null,
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null, status: isAdmin ? status : undefined };
      const r = await fetch(`${API_URL}/api/blog/posts${postId ? `/${postId}` : ""}`, { method: postId ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : t("common.error"));
      toast.success(d.status === "published" ? t("blog.published") : d.status === "pending" ? t("blog.sentForReview") : t("blog.saved"));
      navigate(`/blog/${d.slug}`);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4 px-1 sm:px-0 pb-10" data-testid="blog-editor-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-2" />{t("common.back")}</Button>
      <h1 className="text-2xl sm:text-3xl font-black tracking-tight">{postId ? t("blog.editPost") : t("blog.newPost")}</h1>
      {!isAdmin && <p className="text-sm text-muted-foreground rounded-lg bg-muted/30 p-3" data-testid="blog-moderation-note">{t("blog.moderationNote")}</p>}

      <Card className="glass"><CardContent className="pt-5 space-y-4">
        <div className="space-y-1.5"><Label>{t("blog.fields.title")} *</Label><Input value={form.title} onChange={set("title")} maxLength={200} data-testid="blog-title-input" /></div>
        <div className="space-y-1.5"><Label>{t("blog.fields.excerpt")}</Label><Input value={form.excerpt} onChange={set("excerpt")} maxLength={400} data-testid="blog-excerpt-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{t("blog.fields.category")}</Label>
            <select value={form.category} onChange={set("category")} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="blog-category-select">
              {Object.keys(CATEGORY_LABELS).map((c) => <option key={c} value={c}>{catLabel(c, i18n.language)}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label>{t("blog.fields.lang")}</Label>
            <select value={form.lang} onChange={set("lang")} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="blog-lang-select">
              <option value="ru">Русский</option><option value="en">English</option><option value="zh">中文</option>
            </select></div>
        </div>
        <div className="space-y-1.5"><Label>{t("blog.fields.content")} *</Label><Textarea value={form.content} onChange={set("content")} rows={12} data-testid="blog-content-input" /></div>

        <div className="space-y-1.5">
          <Label>{t("blog.fields.cover")}</Label>
          {form.cover_url ? (
            <div className="relative"><img src={mediaUrl(form.cover_url)} alt="" className="w-full max-h-56 object-cover rounded-lg" />
              <button onClick={() => setForm({ ...form, cover_url: "" })} className="absolute top-2 right-2 rounded-full bg-black/60 p-1"><X className="h-4 w-4 text-white" /></button></div>
          ) : (
            <label className="flex items-center gap-2 h-10 rounded-md border border-dashed border-border px-3 text-sm text-muted-foreground cursor-pointer hover:border-primary">
              {uploading === "cover" ? <><Loader2 className="h-4 w-4 animate-spin" />{progress}%</> : <><Upload className="h-4 w-4" />{t("blog.uploadImage")}</>}
              <input type="file" accept="image/*" className="hidden" onChange={upload("cover")} data-testid="blog-cover-input" />
            </label>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{t("blog.fields.video")}</Label>
          <Input value={form.video_url} onChange={set("video_url")} placeholder="https://youtube.com/… · vk.com/video… · rutube.ru/video/…" data-testid="blog-video-input" />
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer hover:text-primary w-fit">
            {uploading === "video" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{progress}%</> : <><Upload className="h-3.5 w-3.5" />{t("blog.uploadVideo")}</>}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload("video")} data-testid="blog-video-file-input" />
          </label>
        </div>

        <div className="space-y-1.5"><Label>{t("blog.fields.tags")}</Label><Input value={form.tags} onChange={set("tags")} placeholder="дебют, сицилианская, видео" data-testid="blog-tags-input" /></div>
        {isAdmin && <div className="space-y-1.5"><Label>{t("blog.fields.publishAt")}</Label><Input type="datetime-local" value={form.publish_at} onChange={set("publish_at")} data-testid="blog-publish-at-input" /></div>}

        <div className="flex gap-2 pt-2">
          {isAdmin ? (
            <>
              <Button variant="outline" onClick={() => submit("draft")} disabled={saving} data-testid="blog-save-draft-btn"><Save className="h-4 w-4 mr-2" />{t("blog.saveDraft")}</Button>
              <Button onClick={() => submit("published")} disabled={saving} className="flex-1" data-testid="blog-publish-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{t("blog.publish")}</>}</Button>
            </>
          ) : (
            <Button onClick={() => submit()} disabled={saving} className="flex-1" data-testid="blog-submit-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{t("blog.submitForReview")}</>}</Button>
          )}
        </div>
      </CardContent></Card>
    </div>
  );
}
